import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Loader2, Save, Send, CheckCircle2, AlertCircle, ArrowLeft, Lock, Calendar, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, toLocalISODate } from '../../lib/formatUtils';

const QUESTIONS = [
  { key: 'key_contributions', label: '1. Key Contributions', help: 'List your top achievements, deliverables, or projects this year.' },
  { key: 'what_went_well', label: '2. What Went Well', help: 'What aspects of your work or role do you feel proud of?' },
  { key: 'challenges', label: '3. Challenges Faced', help: 'What were the hardest parts of your role this year and how did you handle them?' },
  { key: 'learning_growth', label: '4. Learning & Growth', help: 'What new skills, tools, or perspectives did you pick up?' },
  { key: 'next_year_goals', label: '5. Goals for Next Year', help: 'What do you want to achieve or focus on in the upcoming year?' },
  { key: 'support_needed', label: '6. Support Needed', help: 'What support, training, or resources would help you grow further?' },
  { key: 'compensation_expectation', label: '7. Compensation Expectation', help: 'Share your expectation for revised compensation, if any (with reasoning).', optional: true },
  { key: 'feedback_for_company', label: '8. Feedback for the Company', help: 'Anything you would like leadership to know — culture, processes, leadership, etc.', optional: true },
];

const GUIDELINES = [
  'Keep your answers short and to the point (2–4 lines per question is enough)',
  'Be honest and specific — this is not a test, it helps us improve together',
  'Focus on actual work, outcomes, and challenges (avoid generic statements)',
  'Mention examples where possible (projects, features, responsibilities)',
  'This review will be used for your appraisal discussion, so clarity helps',
];

export const MySelfReviewPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cycle, setCycle] = useState(null);
  const [review, setReview] = useState(null);
  const [form, setForm] = useState(QUESTIONS.reduce((a, q) => ({ ...a, [q.key]: '' }), {}));

  const loadReview = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/appraisals/self-review/my');
      if (r.data.success) {
        setCycle(r.data.cycle);
        setReview(r.data.data);
        if (r.data.data) {
          const next = {};
          QUESTIONS.forEach(q => { next[q.key] = r.data.data[q.key] || ''; });
          setForm(next);
        }
      }
    } catch {
      toast.error('Failed to load self-review');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadReview(); }, [loadReview]);

  const isSubmitted = review?.status === 'submitted';
  const deadlinePassed = cycle?.deadline ? new Date(cycle.deadline) < new Date(toLocalISODate()) : false;

  const handleSave = async (action) => {
    if (!cycle) return;
    if (action === 'submit') {
      const empty = QUESTIONS.filter(q => !q.optional && !form[q.key]?.trim()).map(q => q.label);
      if (empty.length > 0) {
        toast.error(`Please answer all required questions before submitting (${empty.length} pending).`);
        return;
      }
    }
    if (action === 'submit') setSubmitting(true);
    else setSaving(true);
    try {
      await api.post('/appraisals/self-review', { cycle_id: cycle.id, action, ...form });
      toast.success(action === 'submit' ? 'Self-review submitted successfully' : 'Draft saved');
      loadReview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save self-review');
    } finally {
      setSaving(false);
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  if (!cycle) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="my-self-review-empty">
        <Button variant="ghost" size="sm" onClick={() => navigate('/my-portal')} data-testid="back-portal">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Portal
        </Button>
        <Card>
          <CardContent className="py-16 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
            <h2 className="text-lg font-semibold">No Active Appraisal Cycle</h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              You haven't been included in an active appraisal cycle yet. Once HR initiates one, you'll receive an email and will be able to fill your self-review here.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-self-review-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/my-portal')} data-testid="back-portal">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold">My Self-Review</h1>
          <p className="text-muted-foreground text-sm">Annual performance self-assessment for {cycle.fy}</p>
        </div>
      </div>

      {/* Status Banner */}
      <Card className={isSubmitted ? 'border-emerald-500/40 bg-emerald-500/5' : (deadlinePassed ? 'border-red-500/40 bg-red-500/5' : 'border-amber-500/40 bg-amber-500/5')}>
        <CardContent className="py-4 flex items-center gap-4">
          {isSubmitted ? (
            <CheckCircle2 className="h-8 w-8 text-emerald-500 flex-shrink-0" />
          ) : (
            <Calendar className={`h-8 w-8 flex-shrink-0 ${deadlinePassed ? 'text-red-500' : 'text-amber-500'}`} />
          )}
          <div className="flex-1">
            {isSubmitted ? (
              <>
                <p className="font-semibold">Submitted</p>
                <p className="text-sm text-muted-foreground">Your self-review was submitted on {formatDate(review.submitted_at)}. It is now locked and cannot be edited.</p>
              </>
            ) : deadlinePassed ? (
              <>
                <p className="font-semibold text-red-500">Deadline Passed</p>
                <p className="text-sm text-muted-foreground">The submission deadline ({formatDate(cycle.deadline)}) has passed. Please contact HR if you still need to submit.</p>
              </>
            ) : (
              <>
                <p className="font-semibold">Submission Deadline: {formatDate(cycle.deadline)}</p>
                <p className="text-sm text-muted-foreground">{review?.status === 'draft' ? 'Draft saved. Continue editing and submit when ready.' : 'Please complete all 8 questions and submit before the deadline.'}</p>
              </>
            )}
          </div>
          {isSubmitted && <Lock className="h-5 w-5 text-emerald-500" />}
          {review?.status === 'draft' && !isSubmitted && <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/30">Draft</Badge>}
        </CardContent>
      </Card>

      {!isSubmitted && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-sm">
            Your responses will be reviewed by your manager during your 1:1 appraisal. Be honest, specific, and use examples wherever possible.
          </AlertDescription>
        </Alert>
      )}

      {!isSubmitted && (
        <Card className="border-primary/30 bg-primary/5" data-testid="guidelines-card">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Lightbulb className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 space-y-2">
                <p className="font-semibold text-sm">How to fill this form</p>
                <ul className="space-y-1 text-sm text-muted-foreground list-disc pl-5">
                  {GUIDELINES.map((g, i) => <li key={i}>{g}</li>)}
                </ul>
                <p className="text-xs text-foreground/80 pt-2 border-t border-primary/15 mt-2">
                  <span className="font-semibold">Note:</span> There are no right or wrong answers. Honest input will help us make better decisions for your growth and compensation.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Questions */}
      <div className="space-y-4">
        {QUESTIONS.map((q) => {
          const value = form[q.key] || '';
          const len = value.length;
          const overLimit = len > 1000;
          const nearLimit = len >= 900 && !overLimit;
          return (
            <Card key={q.key} data-testid={`q-${q.key}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-base">{q.label}</CardTitle>
                  {q.optional && (
                    <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-amber-500/40 text-amber-600 bg-amber-500/5 flex-shrink-0" data-testid={`optional-${q.key}`}>
                      Optional but recommended
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{q.help}</p>
              </CardHeader>
              <CardContent>
                <Textarea
                  rows={4}
                  value={value}
                  onChange={(e) => setForm({ ...form, [q.key]: e.target.value })}
                  disabled={isSubmitted}
                  maxLength={1000}
                  placeholder={isSubmitted ? '' : 'Type your answer here...'}
                  data-testid={`input-${q.key}`}
                />
                <div className={`mt-1.5 text-xs text-right ${overLimit ? 'text-red-500 font-semibold' : nearLimit ? 'text-amber-500' : 'text-muted-foreground'}`} data-testid={`count-${q.key}`}>
                  {len} / 1000
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Actions */}
      {!isSubmitted && !deadlinePassed && (
        <div className="flex flex-col sm:flex-row gap-3 sticky bottom-4 bg-background/80 backdrop-blur p-4 border rounded-lg shadow-sm">
          <Button variant="outline" onClick={() => handleSave('draft')} disabled={saving || submitting} className="flex-1" data-testid="save-draft-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save as Draft
          </Button>
          <Button onClick={() => handleSave('submit')} disabled={saving || submitting} className="flex-1" data-testid="submit-btn">
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Submit Final Review
          </Button>
        </div>
      )}
    </div>
  );
};
